import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Minus, Plus } from "lucide-react";

const MAX_ZOOM = 4;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const coverScale = (n, w, h) => Math.max(w / n.w, h / n.h);

/**
 * Fixed-aspect cropper. Defaults to a square, circle-masked avatar (512px JPEG);
 * pass aspect / shape / outputWidth / fileName / title for other uses (e.g. a 3:1 cover).
 * Drag to pan, pinch / wheel / slider to zoom, arrow keys + / - for keyboard use.
 * Calls onConfirm(File), or onCancel().
 */
export default function ImageCropper({
  src,
  onConfirm,
  onCancel,
  aspect = 1,
  shape = "circle",
  outputWidth = 512,
  quality = 0.9,
  fileName = "avatar.jpg",
  title,
  uploading = false,
}) {
  const { t } = useTranslation();
  const round = shape === "circle";
  const boxRef = useRef(null);
  const pointers = useRef(new Map());
  const pinchStart = useRef(null);

  const [natural, setNatural] = useState(null); // { w, h }
  const [boxW, setBoxW] = useState(0);
  const [boxH, setBoxH] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const box = boxW > 0 && boxH > 0;
  const base = natural && box ? coverScale(natural, boxW, boxH) : 1;
  const scale = base * zoom;

  // Keep the image covering the whole crop area.
  const clampOffset = useCallback(
    (o, z) => {
      if (!natural || !boxW || !boxH) return o;
      const s = coverScale(natural, boxW, boxH) * z;
      const mx = Math.max(0, (natural.w * s - boxW) / 2);
      const my = Math.max(0, (natural.h * s - boxH) / 2);
      return { x: clamp(o.x, -mx, mx), y: clamp(o.y, -my, my) };
    },
    [natural, boxW, boxH]
  );

  const applyZoom = useCallback(
    (z) => {
      const next = clamp(z, 1, MAX_ZOOM);
      setZoom(next);
      setOffset((o) => clampOffset(o, next));
    },
    [clampOffset]
  );

  // Measure the crop square.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      setBoxW(el.clientWidth);
      setBoxH(el.clientHeight);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-clamp when the box is resized (rotation, keyboard opening, ...).
  useEffect(() => {
    setOffset((o) => clampOffset(o, zoom));
  }, [boxW, boxH, zoom, clampOffset]);

  // Lock page scroll while open, Escape closes.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onCancel]);

  // Wheel zoom (needs a non-passive listener to stop the page scrolling).
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom((z) => {
        const next = clamp(z * Math.exp(-e.deltaY * 0.0015), 1, MAX_ZOOM);
        setOffset((o) => clampOffset(o, next));
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [clampOffset]);

  const dist = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setDragging(true);
    if (pointers.current.size === 2) {
      pinchStart.current = { d: dist(), zoom };
    }
  };

  const onPointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);

    if (pointers.current.size >= 2 && pinchStart.current) {
      applyZoom(pinchStart.current.zoom * (dist() / pinchStart.current.d));
    } else {
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      setOffset((o) => clampOffset({ x: o.x + dx, y: o.y + dy }, zoom));
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    pinchStart.current = null;
    if (pointers.current.size === 0) setDragging(false);
  };

  const onKeyDown = (e) => {
    const step = 16;
    const moves = {
      ArrowLeft: [step, 0],
      ArrowRight: [-step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    if (moves[e.key]) {
      e.preventDefault();
      const [dx, dy] = moves[e.key];
      setOffset((o) => clampOffset({ x: o.x + dx, y: o.y + dy }, zoom));
    } else if (e.key === "+" || e.key === "=") {
      applyZoom(zoom + 0.1);
    } else if (e.key === "-") {
      applyZoom(zoom - 0.1);
    }
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };

  const confirm = () => {
    if (!natural || !box || busy || uploading) return;
    setBusy(true);
    const img = new Image();
    img.onload = () => {
      // Top-left of the image inside the crop area, then map the area back to source pixels.
      const left = boxW / 2 + offset.x - (natural.w * scale) / 2;
      const top = boxH / 2 + offset.y - (natural.h * scale) / 2;
      const sw = boxW / scale;
      const sh = boxH / scale;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(Math.min(outputWidth, Math.round(sw)), 64);
      canvas.height = Math.max(Math.round(canvas.width / aspect), 1);
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        img,
        -left / scale,
        -top / scale,
        sw,
        sh,
        0,
        0,
        canvas.width,
        canvas.height
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            setBusy(false);
            return;
          }
          setBusy(false);
          onConfirm(new File([blob], fileName, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => setBusy(false);
    img.src = src;
  };

  const pct = ((zoom - 1) / (MAX_ZOOM - 1)) * 100;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-slate-950/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t("Crop photo")}
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className={(round ? "sm:max-w-md " : "sm:max-w-2xl ") + "w-full bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col max-h-[100dvh] overflow-y-auto pb-[env(safe-area-inset-bottom)]"}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">{title || t("Crop photo")}</h2>
            <p className="text-xs text-slate-500">
              {t("Drag to reposition, pinch or use the slider to zoom")}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t("Close")}
            className="w-10 h-10 -mr-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Crop area */}
        <div className="px-5">
          <div
            ref={boxRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className={
              "relative mx-auto overflow-hidden rounded-2xl bg-slate-900 select-none outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 " +
              (dragging ? "cursor-grabbing" : "cursor-grab")
            }
            style={{
              width: round ? "min(100%, 55vh)" : `min(100%, calc(55vh * ${aspect}))`,
              aspectRatio: String(aspect),
              touchAction: "none",
            }}
          >
            {failed ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-300 px-6 text-center">
                {t("This image could not be opened")}
              </div>
            ) : (
              <img
                src={src}
                alt=""
                draggable={false}
                onLoad={(e) =>
                  setNatural({
                    w: e.currentTarget.naturalWidth,
                    h: e.currentTarget.naturalHeight,
                  })
                }
                onError={() => setFailed(true)}
                className="absolute left-1/2 top-1/2 max-w-none pointer-events-none"
                style={
                  natural
                    ? {
                        width: natural.w,
                        height: natural.h,
                        transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                        transformOrigin: "center",
                      }
                    : { opacity: 0 }
                }
              />
            )}

            {/* Circular mask + rule-of-thirds guide */}
            {round && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at center, transparent 0, transparent calc(50% - 1px), rgba(2,6,23,.62) 50%)",
                }}
              />
            )}
            <div
              className={
                "absolute inset-0 pointer-events-none ring-2 ring-white/80 " +
                (round ? "rounded-full" : "rounded-2xl")
              }
            />
            <div
              className={
                "absolute inset-0 pointer-events-none overflow-hidden transition-opacity duration-150 " +
                (round ? "rounded-full " : "") +
                (dragging ? "opacity-100" : "opacity-0")
              }
            >
              <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/40" />
              <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/40" />
              <div className="absolute top-1/3 left-0 right-0 h-px bg-white/40" />
              <div className="absolute top-2/3 left-0 right-0 h-px bg-white/40" />
            </div>
          </div>
        </div>

        {/* Zoom control */}
        <div className="px-5 pt-4 flex items-center gap-3">
          <button
            type="button"
            aria-label={t("Zoom out")}
            onClick={() => applyZoom(zoom - 0.25)}
            className="w-10 h-10 shrink-0 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center transition"
          >
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="range"
            min="1"
            max={MAX_ZOOM}
            step="0.01"
            value={zoom}
            onChange={(e) => applyZoom(parseFloat(e.target.value))}
            aria-label={t("Zoom")}
            className="flex-1 h-2 rounded-full appearance-none cursor-pointer accent-indigo-600"
            style={{
              background: `linear-gradient(to right, #4f46e5 ${pct}%, #e2e8f0 ${pct}%)`,
            }}
          />
          <button
            type="button"
            aria-label={t("Zoom in")}
            onClick={() => applyZoom(zoom + 0.25)}
            className="w-10 h-10 shrink-0 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center transition"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 px-5 py-5">
          <button
            type="button"
            onClick={reset}
            className="h-12 px-4 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition"
          >
            {t("Reset")}
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onCancel}
            className="h-12 px-5 rounded-xl text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={!natural || busy || uploading}
            className="h-12 px-6 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition"
          >
            {busy || uploading ? t("Saving...") : t("Apply")}
          </button>
        </div>
      </div>
    </div>
  );
}
